import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './contexts/AuthProvider';
import { OrgProvider } from './contexts/OrgContext';
import { SpacetimeProvider } from './contexts/SpacetimeProvider';
import { AuthGate } from './components/AuthGate';
import { LandingPage } from './components/Landing/LandingPage';
import { PresenceHeartbeat } from './components/PresenceHeartbeat';
import { TweaksProvider } from './hooks/useTweaks';
import { DialogProvider } from './components/dialogs/DialogProvider';
import { LoginRoute } from './auth/LoginRoute';
import { LOGIN_PATH } from './auth/loginEndpoint';
import { captureAuthCallbackLoad } from './auth/authCallbackNotice';
import { parseClientIdList, purgeStaleWorkosSessions } from './auth/workosBootPurge';
import { installSessionLog } from 'turbotwin-sdk/bug-report';
import './styles.css';

// Start capturing console output before anything else runs so the bug-report
// dialog's session.log attachment includes boot, auth and connection logs.
installSessionLog({ appName: 'MineTwin portal', build: import.meta.env.VITE_APP_VERSION });

// Both run before AuthKitProvider mounts (it reads storage and strips `?code=`
// from the URL once it starts): drop refresh tokens that belong to another
// WorkOS client id, and remember whether this load is a sign-in callback.
purgeStaleWorkosSessions({
  clientId: import.meta.env.VITE_WORKOS_CLIENT_ID,
  spareClientIds: parseClientIdList(import.meta.env.VITE_WORKOS_SPARE_REFRESH_CLIENT_IDS),
});
captureAuthCallbackLoad();

/**
 * Outer router. One top-level surface:
 *
 *   /*  → Operator + executive console (WorkOS-gated). Internal navigation
 *         inside `<App />` is still view-state today; can be migrated to
 *         nested routes incrementally.
 *
 * The /orient/:token trainee invite landing (MVP-2 Story 6, R1) is unrouted
 * until Story 5 gives it real invite tokens — it only ever ran on hardcoded
 * fixtures, nothing generates /orient links, and a fake-working page is a
 * demo hazard (demo-readiness: "finished or hidden"). The component lives on
 * in components/Trainee/InviteLanding.tsx; unknown /orient URLs fall through
 * to the sign-in landing via the /* catch-all.
 *
 * AuthProvider wraps everything so AuthKit's callback handling on /callback
 * keeps working.
 *
 * /login is the WorkOS Sign-in endpoint (auth/loginEndpoint.ts). It is an
 * exact match, deliberately not `/login/*`: App writes `/<org>/<site>/<view>`
 * paths from the org slug, so a wildcard here would loop on the URLs of an org
 * whose slug is `login`.
 */
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      {/* Portal-styled confirm/prompt/alert host — one for the whole tree,
          above every route, so no page reaches for window.confirm. */}
      <DialogProvider>
        <BrowserRouter>
          <Routes>
            <Route path={LOGIN_PATH} element={<LoginRoute />} />
            <Route
              path="/*"
              element={
                <AuthGate landing={<LandingPage />}>
                  {/* OrgProvider gates the whole connected tree: children render
                      only once the org is selected AND resolved to a slug, so
                      SpacetimeProvider can derive module names unconditionally
                      (MULTI-ORG-PORTAL plan). */}
                  <OrgProvider>
                    <TweaksProvider>
                      <SpacetimeProvider>
                        <PresenceHeartbeat />
                        <App />
                      </SpacetimeProvider>
                    </TweaksProvider>
                  </OrgProvider>
                </AuthGate>
              }
            />
          </Routes>
        </BrowserRouter>
      </DialogProvider>
    </AuthProvider>
  </StrictMode>,
);
